import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useProject } from '../lib/projects';
import {
  useCreateSection,
  useCreateTask,
  useDeleteSection,
  useDeleteTask,
  useRenameSection,
  useReorderTasks,
  useSections,
  useTasks,
  useUpdateTask,
} from '../lib/sections';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { ProjectHeader } from '../components/ProjectHeader';
import { TaskTable } from '../components/TaskTable';
import { TaskCardList } from '../components/TaskCardList';
import { TaskEditModal } from '../components/TaskEditModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ProjectAuditPanel } from '../components/ProjectAuditPanel';
import { TemplateMergeBanner } from '../components/TemplateMergeBanner';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { TodosPanel } from '../components/TodosPanel';
import { PhotosPanel } from '../components/PhotosPanel';
import { SectionCreateModal } from '../components/SectionCreateModal';
import type { CreateSectionInput } from '../lib/sections';
import type { Task, TaskInput } from '@stromo/shared';
import { api } from '../lib/api';

async function downloadProjectPdf(id: number, orderNumber: string) {
  const response = await api.get(`/export/project/${id}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${orderNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = id ? Number(id) : undefined;
  const { user } = useAuthStore();
  // canManage = Sektionen + Tasks-Löschen + Section-Rename. Admin/PM only.
  const canManage = user?.role === 'admin' || user?.role === 'project_manager';
  // canAddTasks = "+ Aufgabe", "+ Gruppe" und der "+ Aufgabe"-Knopf neben
  // einem Gruppen-Header. Bauleiter dürfen Aufgaben anlegen, sonst aber
  // keine Section-/Strukturänderungen.
  const canAddTasks = canManage || user?.role === 'site_manager';
  // Mitarbeiter haben Read-Only auf Aufgaben (Tasks). Bauleiter darf
  // weiter Status/Termine pflegen — daher hier nur 'employee' sperren.
  const isTasksReadOnly = user?.role === 'employee';

  const projectQ = useProject(projectId);
  const sectionsQ = useSections(projectId);
  const [activeSectionId, setActiveSectionId] = useState<number | null>(null);
  const [deleteSectionTarget, setDeleteSectionTarget] = useState<number | null>(null);
  const [highlightTaskId, setHighlightTaskId] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  // Mobile-Sortier-Modus (↑/↓ statt Drag & Drop) — gilt pro Sektion.
  const [mobileSortMode, setMobileSortMode] = useState(false);
  useEffect(() => setMobileSortMode(false), [activeSectionId]);

  const sectionParam = searchParams.get('section');
  const taskParam = searchParams.get('task');

  // Bei ?section=X aus URL: dorthin wechseln. Sonst erste Sektion.
  useEffect(() => {
    if (!sectionsQ.data?.length) return;
    if (sectionParam) {
      const target = sectionsQ.data.find((s) => s.id === Number(sectionParam));
      if (target) {
        setActiveSectionId(target.id);
        return;
      }
    }
    if (activeSectionId == null) {
      const first = sectionsQ.data[0];
      if (first) setActiveSectionId(first.id);
    }
  }, [sectionsQ.data, sectionParam, activeSectionId]);

  const tasksQ = useTasks(activeSectionId ?? undefined);

  // Highlight + Scroll, sobald Tasks zur richtigen Section geladen sind
  useEffect(() => {
    if (!taskParam || !tasksQ.data) return;
    const taskId = Number(taskParam);
    if (!tasksQ.data.some((t) => t.id === taskId)) return;
    setHighlightTaskId(taskId);
    const row = document.getElementById(`task-row-${taskId}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => {
      setHighlightTaskId(null);
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      next.delete('section');
      setSearchParams(next, { replace: true });
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [tasksQ.data, taskParam, searchParams, setSearchParams]);

  const createSection = useCreateSection(projectId ?? 0);
  const renameSection = useRenameSection(projectId ?? 0);
  const deleteSection = useDeleteSection(projectId ?? 0);

  const createTask = useCreateTask(activeSectionId ?? 0, projectId);
  const updateTask = useUpdateTask(activeSectionId ?? 0, projectId);
  const deleteTask = useDeleteTask(activeSectionId ?? 0, projectId);
  const reorderTasks = useReorderTasks(activeSectionId ?? 0, projectId);

  const activeSection = useMemo(
    () => sectionsQ.data?.find((s) => s.id === activeSectionId) ?? null,
    [sectionsQ.data, activeSectionId],
  );

  const handleAddTask = () => {
    if (!activeSectionId) return;
    const nextPos =
      (tasksQ.data ?? []).filter((t) => !t.is_group_header).reduce((max, t) => Math.max(max, t.position_number), 0) + 1;
    const input: TaskInput = {
      title: 'Neue Aufgabe',
      position_number: nextPos,
      status: 'open',
      progress_percent: 0,
      is_group_header: false,
    };
    createTask.mutate(input);
  };

  /**
   * "+ Aufgabe"-Knopf neben einem Gruppen-Header: hängt die neue Aufgabe ans
   * Ende dieser Gruppe (= direkt vor dem nächsten Gruppen-Header oder ans
   * Ende der Liste, wenn keine weitere Gruppe folgt). Erspart das spätere
   * manuelle Verschieben.
   */
  const handleAddTaskAfterGroup = (groupTaskId: number) => {
    if (!activeSectionId) return;
    const list = tasksQ.data ?? [];
    const groupIdx = list.findIndex((t) => t.id === groupTaskId);
    if (groupIdx === -1) return;

    // Letzte Task innerhalb dieser Gruppe finden (alles bis zum nächsten
    // Gruppen-Header). Wenn die Gruppe leer ist, ist die Gruppe selbst der
    // Anker — der Server fügt dann direkt nach dem Header ein.
    let anchorId = groupTaskId;
    for (let i = groupIdx + 1; i < list.length; i += 1) {
      const t = list[i];
      if (!t) break;
      if (t.is_group_header) break;
      anchorId = t.id;
    }

    const nextPos =
      list.filter((t) => !t.is_group_header).reduce((max, t) => Math.max(max, t.position_number), 0) + 1;
    createTask.mutate({
      input: {
        title: 'Neue Aufgabe',
        position_number: nextPos,
        status: 'open',
        progress_percent: 0,
        is_group_header: false,
      },
      afterTaskId: anchorId,
    });
  };

  const handleReorderTasks = (orderedIds: number[]) => {
    if (!activeSectionId) return;
    reorderTasks.mutate(orderedIds);
  };

  const handleAddGroup = () => {
    if (!activeSectionId) return;
    const input: TaskInput = {
      title: 'Neue Gruppe',
      is_group_header: true,
      position_number: 0,
      status: 'open',
      progress_percent: 0,
    };
    createTask.mutate(input);
  };

  const handleAddSection = () => {
    setSectionModalOpen(true);
  };

  const handleSubmitNewSection = (input: CreateSectionInput) => {
    createSection.mutate(input, {
      onSuccess: (s) => {
        setActiveSectionId(s.id);
        setSectionModalOpen(false);
      },
    });
  };

  const defaultSectionName = `Haus ${(sectionsQ.data?.length ?? 0) + 1}`;

  const handleRenameSection = () => {
    if (!activeSection) return;
    const name = window.prompt('Neuer Sektionsname:', activeSection.name);
    if (!name || name === activeSection.name) return;
    renameSection.mutate({ id: activeSection.id, name });
  };

  const handleDeleteSection = () => {
    if (!deleteSectionTarget) return;
    deleteSection.mutate(deleteSectionTarget, {
      onSuccess: () => {
        setDeleteSectionTarget(null);
        if (deleteSectionTarget === activeSectionId) {
          const remaining = (sectionsQ.data ?? []).filter((s) => s.id !== deleteSectionTarget);
          setActiveSectionId(remaining[0]?.id ?? null);
        }
      },
    });
  };

  if (projectQ.isLoading || sectionsQ.isLoading) {
    return (
      <>
        <header className="topbar"><div className="topbar-title">Projekt-Detail</div></header>
        <div className="content"><div className="loading">Lade …</div></div>
      </>
    );
  }

  if (projectQ.error || !projectQ.data) {
    return (
      <>
        <header className="topbar"><div className="topbar-title">Projekt-Detail</div></header>
        <div className="content">
          <div className="empty-state">
            <p>Projekt nicht gefunden.</p>
            <Link to="/projects" className="btn btn-secondary" style={{ marginTop: 12 }}>Zurück zur Übersicht</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">
          <Link to="/projects" style={{ color: 'var(--color-text-soft)', fontWeight: 400, marginRight: 8 }}>Projekte</Link>
          <span style={{ color: 'var(--color-text-soft)' }}>›</span>
          <span style={{ marginLeft: 8 }}>{projectQ.data.order_number}</span>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => downloadProjectPdf(projectQ.data.id, projectQ.data.order_number)}
          >
            PDF-Export
          </button>
        </div>
      </header>

      <div className="content">
        <ProjectHeader project={projectQ.data} />

        {projectId && <TemplateMergeBanner projectId={projectId} canManage={canManage} />}

        <div className="card" style={{ padding: 0, overflowX: 'hidden' }}>
          <div className="tabs" style={{
            display: 'flex',
            gap: 0,
            borderBottom: '2px solid var(--color-border-soft)',
            paddingLeft: 20,
            paddingRight: 20,
          }}>
            {(sectionsQ.data ?? []).map((s) => (
              <button
                key={s.id}
                type="button"
                className={`tab${s.id === activeSectionId ? ' active' : ''}`}
                onClick={() => setActiveSectionId(s.id)}
                style={{
                  padding: '9px 18px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: s.id === activeSectionId ? 'var(--color-primary)' : 'var(--color-text-soft)',
                  cursor: 'pointer',
                  borderBottom: s.id === activeSectionId ? '2px solid var(--color-primary)' : '2px solid transparent',
                  marginBottom: -2,
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: 2,
                  borderBottomStyle: 'solid',
                  borderBottomColor: s.id === activeSectionId ? 'var(--color-primary)' : 'transparent',
                  transition: 'all .15s',
                }}
              >
                {s.name}
              </button>
            ))}
            {canManage && (
              <button
                type="button"
                onClick={handleAddSection}
                style={{
                  padding: '9px 14px',
                  fontSize: 13,
                  color: 'var(--color-text-soft)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginLeft: 'auto',
                }}
                title="Sektion hinzufügen"
              >
                + Sektion
              </button>
            )}
          </div>

          <div style={{ padding: 20 }}>
            {activeSection && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-navy)' }}>
                  {activeSection.name}
                  {' '}
                  <span style={{ color: 'var(--color-text-soft)', fontWeight: 400, fontSize: 12 }}>
                    ({(tasksQ.data ?? []).filter((t) => !t.is_group_header).length} Aufgaben)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canManage && (tasksQ.data?.length ?? 0) > 1 && (
                    <button
                      type="button"
                      className={`btn btn-sm show-on-mobile ${mobileSortMode ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setMobileSortMode((m) => !m)}
                    >
                      {mobileSortMode ? 'Fertig' : 'Sortieren'}
                    </button>
                  )}
                  {canAddTasks && (
                    <>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddTask}>
                        + Aufgabe
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddGroup}>
                        + Gruppe
                      </button>
                    </>
                  )}
                  {canManage && (
                    <>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={handleRenameSection}>
                        Umbenennen
                      </button>
                      {(sectionsQ.data?.length ?? 0) > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteSectionTarget(activeSection.id)}
                        >
                          Sektion löschen
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {tasksQ.isLoading ? (
              <div className="loading">Lade Aufgaben …</div>
            ) : tasksQ.error ? (
              <div className="empty-state"><p>Fehler: {apiErrorMessage(tasksQ.error)}</p></div>
            ) : (
              <>
                {/* Desktop / Tablet: inline-editable Tabelle */}
                <div className="hide-on-mobile">
                  <TaskTable
                    tasks={tasksQ.data ?? []}
                    onUpdate={(taskId, patch) => updateTask.mutate({ id: taskId, patch })}
                    onDelete={canManage ? (taskId) => deleteTask.mutate(taskId) : undefined}
                    onReorder={canManage ? handleReorderTasks : undefined}
                    onAddTaskAfter={canAddTasks ? handleAddTaskAfterGroup : undefined}
                    readOnly={isTasksReadOnly}
                    highlightTaskId={highlightTaskId}
                  />
                </div>

                {/* Mobile (≤640px): Card-Stack mit Tap-to-edit */}
                <div className="show-on-mobile">
                  <TaskCardList
                    tasks={tasksQ.data ?? []}
                    onTaskClick={(t) => setEditingTask(t)}
                    highlightTaskId={highlightTaskId}
                    sortMode={mobileSortMode}
                    onReorder={canManage ? handleReorderTasks : undefined}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {projectId && <TodosPanel projectId={projectId} />}
        {projectId && <DocumentsPanel projectId={projectId} />}
        {projectId && <PhotosPanel projectId={projectId} />}
        {projectId && <ProjectAuditPanel projectId={projectId} />}
      </div>

      <ConfirmDialog
        open={deleteSectionTarget != null}
        title="Sektion löschen"
        danger
        message="Sektion und alle enthaltenen Aufgaben endgültig löschen?"
        confirmLabel="Endgültig löschen"
        onCancel={() => setDeleteSectionTarget(null)}
        onConfirm={handleDeleteSection}
      />

      <TaskEditModal
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSave={(patch) => {
          if (!editingTask) return;
          updateTask.mutate(
            { id: editingTask.id, patch },
            { onSuccess: () => setEditingTask(null) },
          );
        }}
        onDelete={
          canManage
            ? (taskId) => {
                deleteTask.mutate(taskId, { onSuccess: () => setEditingTask(null) });
              }
            : undefined
        }
        saving={updateTask.isPending || deleteTask.isPending}
        readOnly={isTasksReadOnly}
      />

      <SectionCreateModal
        open={sectionModalOpen}
        sections={sectionsQ.data ?? []}
        defaultName={defaultSectionName}
        saving={createSection.isPending}
        errorMessage={createSection.isError ? apiErrorMessage(createSection.error) : null}
        onCancel={() => setSectionModalOpen(false)}
        onSubmit={handleSubmitNewSection}
      />
    </>
  );
}
